import React, { useState, useEffect } from 'react';
import {
  collection, addDoc, updateDoc, deleteDoc, doc,
  onSnapshot, query, where, setDoc,
} from 'firebase/firestore';
import { db, auth } from './firebase';
import { useAuth } from './context/AuthContext';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { generarReportePDF } from './utils/generarPDF';
import Login from './pages/Login';
import Sidebar from './components/Sidebar';
import SeguimientoModal from './components/SeguimientoModal';
import Dashboard from './pages/Dashboard';
import Casos from './pages/Casos';
import NuevoCaso from './pages/NuevoCaso';
import DetalleCaso from './pages/DetalleCaso';
import EditarCaso from './pages/EditarCaso';
import Alertas from './pages/Alertas';
import Usuarios from './pages/Usuarios';
import Colegios from './pages/Colegios';

function buildAlerts(casos) {
  const alerts = [];
  const hoy = new Date();
  casos.forEach(c => {
    if (c.estado === 'Cerrado' || !c.fechaAlerta) return;
    const dias = Math.ceil((new Date(c.fechaAlerta) - hoy) / (1000 * 60 * 60 * 24));
    if (dias < 0) {
      alerts.push({ id: `v_${c.id}`, tipo: 'urgente', titulo: c.centro, mensaje: `Fecha límite vencida hace ${Math.abs(dias)} día(s).` });
    } else if (dias <= 7) {
      alerts.push({ id: `p_${c.id}`, tipo: 'proximo', titulo: c.centro, mensaje: `Fecha límite en ${dias} día(s). Estado: ${c.estado}` });
    }
  });
  casos.filter(c => !c.destinatario && c.estado !== 'Cerrado').slice(0, 3).forEach(c => {
    alerts.push({ id: `d_${c.id}`, tipo: 'info', titulo: c.centro, mensaje: 'Sin destinatario asignado.' });
  });
  return alerts;
}

export default function App() {
  const { currentUser, userRole, isAdmin } = useAuth();
  const [page, setPage] = useState('dashboard');
  const [casos, setCasos] = useState([]);
  const [casoDetalle, setCasoDetalle] = useState(null);
  const [casoSeguimiento, setCasoSeguimiento] = useState(null);
  const [casoEditar, setCasoEditar] = useState(null);
  const [dismissedAlerts, setDismissedAlerts] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [busquedaOpen, setBusquedaOpen] = useState(false);
  const [modalUsuario, setModalUsuario] = useState(false);
  const [formUsuario, setFormUsuario] = useState({ nombre: '', email: '', password: '', rol: 'tecnico' });
  const [savingUsuario, setSavingUsuario] = useState(false);
  const [errorUsuario, setErrorUsuario] = useState('');
  const [successUsuario, setSuccessUsuario] = useState('');

  // ── Cargar casos según rol ──────────────────────
  useEffect(() => {
    if (!currentUser || userRole === null) return;
    let q;
    if (userRole === 'admin' || userRole === 'tecnico' || userRole === 'secretaria') {
      q = query(collection(db, 'casos'));
    } else {
      q = query(collection(db, 'casos'), where('creadoPor', '==', currentUser.uid));
    }
    const unsub = onSnapshot(q, snap => {
      setCasos(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [currentUser, userRole]);

  // ── Actualizar casoDetalle en tiempo real ──────────────────────
  useEffect(() => {
    if (!casoDetalle) return;
    const updated = casos.find(c => c.id === casoDetalle.id);
    if (updated) setCasoDetalle(updated);
  }, [casos]);

  const alerts = buildAlerts(casos).filter(a => !dismissedAlerts.includes(a.id));
  const pendientes = casos.filter(c => c.estado === 'Recibido').length;

  function verDetalle(id) {
    const c = casos.find(x => x.id === String(id));
    if (c) { setCasoDetalle(c); setPage('detalle'); }
  }

  async function guardarCaso(data) {
    await addDoc(collection(db, 'casos'), { ...data, creadoPor: currentUser.uid });
    setPage('casos');
  }

  async function guardarSeguimiento(casoId, seg, nuevoEstado, nuevoDestino, nuevaAlerta) {
    const ref = doc(db, 'casos', casoId);
    const caso = casos.find(c => c.id === casoId);
    const seguimientos = [...(caso?.seguimientos || []), seg];
    const upd = { seguimientos, estado: nuevoEstado };
    if (nuevoDestino) upd.destinatario = nuevoDestino;
    if (nuevaAlerta) upd.fechaAlerta = nuevaAlerta;
    await updateDoc(ref, upd);
  }

  async function eliminarCaso(id) {
    try {
      await deleteDoc(doc(db, 'casos', id));
      setPage('casos');
      setCasoDetalle(null);
    } catch (e) {
      alert('Error al eliminar el caso: ' + e.message);
    }
  }

  async function crearUsuarioRapido(e) {
    e.preventDefault();
    if (!formUsuario.nombre || !formUsuario.email || !formUsuario.password) {
      setErrorUsuario('Completa todos los campos.'); return;
    }
    if (formUsuario.password.length < 6) {
      setErrorUsuario('La contraseña debe tener mínimo 6 caracteres.'); return;
    }
    setSavingUsuario(true); setErrorUsuario(''); setSuccessUsuario('');
    try {
      const cred = await createUserWithEmailAndPassword(auth, formUsuario.email, formUsuario.password);
      await setDoc(doc(db, 'usuarios', cred.user.uid), {
        email: formUsuario.email, nombre: formUsuario.nombre,
        rol: formUsuario.rol, creadoEn: new Date().toISOString(),
      });
      setSuccessUsuario(`Usuario "${formUsuario.nombre}" creado correctamente.`);
      setFormUsuario({ nombre: '', email: '', password: '', rol: 'tecnico' });
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') setErrorUsuario('Este correo ya está registrado.');
      else if (err.code === 'auth/invalid-email') setErrorUsuario('El correo no es válido.');
      else setErrorUsuario(err.message);
    }
    setSavingUsuario(false);
  }

  if (!currentUser) return <Login />;

  const PAGE_TITLES = {
    dashboard: 'Panel principal', casos: 'Casos registrados',
    nuevo: 'Nuevo caso', alertas: 'Alertas', usuarios: 'Usuarios',
    detalle: 'Detalle del caso', editar: 'Editar caso', colegios: 'Gestión de colegios',
  };

  return (
    <div className="app-shell">
      <Sidebar
        active={page} onNavigate={setPage}
        pendientes={pendientes} alertasCount={alerts.length}
        sidebarOpen={sidebarOpen} onClose={() => setSidebarOpen(false)}
        userRole={userRole}
      />
      <div className="main-area">
        <header className="topbar">
          <button className="hamburger" onClick={() => setSidebarOpen(o => !o)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
            </svg>
          </button>
          <span className="topbar-title" style={{ flexShrink: 0 }}>{PAGE_TITLES[page] || ''}</span>

          {/* Búsqueda global */}
          <div style={{ flex: 1, maxWidth: 380, position: 'relative', margin: '0 12px' }}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--gray-400)" strokeWidth="2.5" style={{ position: 'absolute', left: 10, flexShrink: 0 }}>
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                type="text"
                placeholder="Buscar colegio, solicitante, código..."
                value={busqueda}
                onChange={e => { setBusqueda(e.target.value); setBusquedaOpen(e.target.value.length > 0); }}
                onFocus={() => busqueda.length > 0 && setBusquedaOpen(true)}
                onBlur={() => setTimeout(() => setBusquedaOpen(false), 200)}
                style={{ width: '100%', padding: '7px 12px 7px 32px', border: '1.5px solid var(--gray-200)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', background: 'var(--gray-50)', color: 'var(--gray-900)', outline: 'none' }}
              />
              {busqueda && (
                <button onClick={() => { setBusqueda(''); setBusquedaOpen(false); }}
                  style={{ position: 'absolute', right: 8, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', fontSize: 16, lineHeight: 1 }}>×</button>
              )}
            </div>
            {busquedaOpen && busqueda.length > 0 && (() => {
              const q = busqueda.toLowerCase();
              const resultados = casos.filter(c =>
                c.centro?.toLowerCase().includes(q) ||
                c.solicitante?.toLowerCase().includes(q) ||
                c.codigoCentro?.includes(q) ||
                c.descripcion?.toLowerCase().includes(q) ||
                c.destinatario?.toLowerCase().includes(q)
              ).slice(0, 7);
              return (
                <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, background: '#fff', border: '1.5px solid var(--gray-200)', borderRadius: 10, boxShadow: '0 8px 32px rgba(0,45,98,.15)', zIndex: 500, overflow: 'hidden' }}>
                  {resultados.length === 0 ? (
                    <div style={{ padding: '14px 16px', fontSize: 13, color: 'var(--gray-400)', textAlign: 'center' }}>Sin resultados para "{busqueda}"</div>
                  ) : (
                    <>
                      <div style={{ padding: '8px 14px 6px', fontSize: 10, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '.5px', borderBottom: '1px solid var(--gray-100)' }}>
                        {resultados.length} resultado(s)
                      </div>
                      {resultados.map(c => (
                        <div key={c.id} onMouseDown={() => { verDetalle(c.id); setBusqueda(''); setBusquedaOpen(false); }}
                          style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--gray-100)', display: 'flex', alignItems: 'center', gap: 10 }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,45,98,.04)'}
                          onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                          {c.codigoCentro && c.codigoCentro !== 'NUEVO' && (
                            <span style={{ background: 'linear-gradient(135deg,#002D62,#0a3d80)', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20, flexShrink: 0 }}>{c.codigoCentro}</span>
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.centro}</div>
                            <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>{c.solicitante} · {c.tipo}</div>
                          </div>
                          <span style={{ fontSize: 10, fontWeight: 700, flexShrink: 0, color: c.estado === 'Recibido' ? '#1d4ed8' : c.estado === 'En proceso' ? '#b45309' : c.estado === 'Orientado' ? '#15803d' : '#6b7280' }}>{c.estado}</span>
                        </div>
                      ))}
                      <div onMouseDown={() => { setPage('casos'); setBusqueda(''); setBusquedaOpen(false); }}
                        style={{ padding: '9px 14px', fontSize: 12, color: 'var(--minerd-blue)', fontWeight: 600, cursor: 'pointer', textAlign: 'center', background: 'rgba(0,45,98,.03)' }}>
                        Ver todos los casos →
                      </div>
                    </>
                  )}
                </div>
              );
            })()}
          </div>

          <button className="btn btn-sm btn-primary" onClick={() => setPage('nuevo')} style={{ flexShrink: 0 }}>+ Nuevo caso</button>
          {isAdmin && (
            <button className="btn btn-sm" onClick={() => { setModalUsuario(true); setErrorUsuario(''); setSuccessUsuario(''); }}
              style={{ flexShrink: 0, background: 'linear-gradient(135deg,#15803d,#009A44)', color: '#fff', border: 'none' }}>
              + Usuario
            </button>
          )}
        </header>

        <main className="page-content">
          {page === 'dashboard' && (
            <Dashboard casos={casos} alerts={alerts}
              onDismissAlert={id => setDismissedAlerts(p => [...p, id])}
              onNavigate={setPage} onVerDetalle={verDetalle}
              onGenerarPDF={() => generarReportePDF(casos)}
            />
          )}
          {page === 'casos' && (
            <Casos casos={casos} onVerDetalle={verDetalle}
              onAbrirSeguimiento={c => setCasoSeguimiento(c)}
              userRole={userRole}
            />
          )}
          {page === 'nuevo' && (
            <NuevoCaso onGuardar={guardarCaso} onCancel={() => setPage('casos')} />
          )}
          {page === 'alertas' && (
            <Alertas casos={casos} onVerDetalle={verDetalle} />
          )}
          {page === 'detalle' && casoDetalle && (
            <DetalleCaso
              caso={casoDetalle} onBack={() => setPage('casos')}
              onAbrirSeguimiento={c => setCasoSeguimiento(c)}
              onEditar={c => { setCasoEditar(c); setPage('editar'); }}
              onEliminar={eliminarCaso}
              onUpdateCaso={c => setCasoDetalle(c)}
              userRole={userRole}
            />
          )}
          {page === 'editar' && casoEditar && (
            <EditarCaso
              caso={casoEditar}
              onGuardar={() => { setPage('detalle'); setCasoEditar(null); }}
              onCancel={() => { setPage('detalle'); setCasoEditar(null); }}
            />
          )}
          {page === 'usuarios' && isAdmin && <Usuarios />}
          {page === 'colegios' && isAdmin && <Colegios />}
        </main>
      </div>

      {casoSeguimiento && (
        <SeguimientoModal
          caso={casoSeguimiento}
          onClose={() => setCasoSeguimiento(null)}
          onGuardar={guardarSeguimiento}
        />
      )}

      {/* Modal crear usuario */}
      {modalUsuario && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={e => e.target === e.currentTarget && setModalUsuario(false)}>
          <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 440, boxShadow: '0 8px 32px rgba(0,0,0,.18)' }}>
            <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: '14px 14px 0 0' }}>
              <h2 style={{ fontSize: 15, fontWeight: 700 }}>Crear nuevo usuario</h2>
              <button onClick={() => setModalUsuario(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: '#9ca3af' }}>×</button>
            </div>
            <div style={{ padding: 20 }}>
              {errorUsuario && <div style={{ background: '#fff0f0', border: '1px solid #fca5a5', borderRadius: 8, padding: '9px 12px', fontSize: 13, color: '#CE1126', marginBottom: 14 }}>{errorUsuario}</div>}
              {successUsuario && <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '9px 12px', fontSize: 13, color: '#15803d', marginBottom: 14, fontWeight: 600 }}>✓ {successUsuario}</div>}
              <form onSubmit={crearUsuarioRapido}>
                <div className="form-group"><label>Nombre completo</label><input value={formUsuario.nombre} onChange={e => setFormUsuario(f => ({ ...f, nombre: e.target.value }))} placeholder="Nombre del usuario" /></div>
                <div className="form-group"><label>Correo electrónico</label><input type="email" value={formUsuario.email} onChange={e => setFormUsuario(f => ({ ...f, email: e.target.value }))} placeholder="correo@ejemplo.com" /></div>
                <div className="form-group"><label>Contraseña</label><input type="password" value={formUsuario.password} onChange={e => setFormUsuario(f => ({ ...f, password: e.target.value }))} placeholder="Mínimo 6 caracteres" /></div>
                <div className="form-group"><label>Rol</label>
                  <select value={formUsuario.rol} onChange={e => setFormUsuario(f => ({ ...f, rol: e.target.value }))}>
                    <option value="admin">Administrador — ve todo</option>
                    <option value="tecnico">Técnico — ve todos los casos</option>
                    <option value="secretaria">Secretaria — solo sus casos</option>
                  </select>
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                  <button type="submit" className="btn btn-primary" disabled={savingUsuario} style={{ flex: 1, justifyContent: 'center' }}>
                    {savingUsuario ? 'Creando...' : 'Crear usuario'}
                  </button>
                  <button type="button" className="btn" onClick={() => setModalUsuario(false)}>Cancelar</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
